// Card types and game logic utilities for Klondike Solitaire

export type Suit = 'spades' | 'hearts' | 'diamonds' | 'clubs'
export type Rank = 'A' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K'

export interface Card {
  id: string
  suit: Suit
  rank: Rank
  faceUp: boolean
}

const SUITS: Suit[] = ['spades', 'hearts', 'diamonds', 'clubs']
const RANKS: Rank[] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K']

export const isRed = (suit: Suit): boolean => suit === 'hearts' || suit === 'diamonds'

export const getSuitSymbol = (suit: Suit): string => {
  switch (suit) {
    case 'spades': return '♠'
    case 'hearts': return '♥'
    case 'diamonds': return '♦'
    case 'clubs': return '♣'
  }
}

export const getRankValue = (rank: Rank): number => {
  if (rank === 'A') return 1
  if (rank === 'J') return 11
  if (rank === 'Q') return 12
  if (rank === 'K') return 13
  return parseInt(rank)
}

export function createDeck(): Card[] {
  const deck: Card[] = []
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      deck.push({
        id: `${suit}-${rank}`,
        suit,
        rank,
        faceUp: false,
      })
    }
  }
  return deck
}

export function shuffleDeck(deck: Card[]): Card[] {
  const shuffled = [...deck]
  // Fisher-Yates shuffle
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!]
  }
  return shuffled
}

export function createShuffledDeck(): Card[] {
  return shuffleDeck(createDeck())
}

export function dealCards(deck: Card[]): { tableau: Card[][], stock: Card[] } {
  const tableau: Card[][] = [[], [], [], [], [], [], []]
  let cardIndex = 0

  // Deal to tableau: column 0 gets 1 card, column 1 gets 2, etc.
  for (let col = 0; col < 7; col++) {
    for (let row = 0; row <= col; row++) {
      const card = { ...deck[cardIndex++]! }
      // Only the top card (last dealt to each column) is face up
      card.faceUp = row === col
      tableau[col]!.push(card)
    }
  }

  // Remaining cards go to stock (24 cards)
  const stock = deck.slice(cardIndex).map(card => ({ ...card, faceUp: false }))

  return { tableau, stock }
}

export function canMoveToFoundation(card: Card, foundation: Card[]): boolean {
  if (foundation.length === 0) {
    // Only Aces can start a foundation
    return card.rank === 'A'
  }

  const topCard = foundation[foundation.length - 1]!
  // Must be same suit and one rank higher
  return card.suit === topCard.suit &&
         getRankValue(card.rank) === getRankValue(topCard.rank) + 1
}

export function canMoveToTableau(movingCard: Card, targetColumn: Card[]): boolean {
  if (targetColumn.length === 0) {
    // Only Kings can go on empty columns
    return movingCard.rank === 'K'
  }

  const topCard = targetColumn[targetColumn.length - 1]!
  // Must be opposite color and one rank lower
  const oppositeColor = isRed(movingCard.suit) !== isRed(topCard.suit)
  const oneRankLower = getRankValue(movingCard.rank) === getRankValue(topCard.rank) - 1

  return oppositeColor && oneRankLower
}
